/**
 * Per-engine spatial predicate builders + the tagged unsupported error.
 *
 * Split out of `spatialFilterSql.ts` (public API) to keep both files small.
 * Dependency-clean (type-only imports) so it is safe to import from the
 * electron main process. Every wire form is pinned by the SPATIAL-FACTS
 * appendix (a6/b4/c4/d2/d3) — NOT guessed. `g` / `lngField` arrive already
 * quoted by the caller; these builders assemble predicates from NUMBERS only.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SpatialFilterContext } from '../../core/types/workspaceTypes';

const UNSUPPORTED_TAG = '__spatialFilterUnsupported';

/**
 * A plain `Error` tagged `__spatialFilterUnsupported: true` (A35/CD15 — a
 * factory, NOT a class, per the repo's no-classes rule).
 */
export function makeSpatialFilterUnsupportedError(engine: DatabaseEngineId): Error {
  const err = new Error(`Spatial filters are not supported for engine "${engine}" with this column.`);
  (err as Error & Record<string, unknown>)[UNSUPPORTED_TAG] = true;
  return err;
}

/** Guard for the tagged unsupported error. */
export function isSpatialFilterUnsupportedError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error as Error & Record<string, unknown>)[UNSUPPORTED_TAG] === true
  );
}

/** Full-precision, locale-free number literal. Rejects non-finite ordinates. */
export function num(n: number, engine: DatabaseEngineId): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) throw makeSpatialFilterUnsupportedError(engine);
  return String(n);
}

/**
 * Validate + return an mssql column SRID for embedding into `STGeomFromText`.
 * A valid SRID is a finite, non-negative integer; `null` (mixed/unknown) or a
 * non-finite value (e.g. `NaN`) throws instead of interpolating `,NaN)` into the
 * SQL. (F4 — the `=== null`-only check let `NaN` through.)
 */
export function mssqlSrid(srid: number | null, engine: DatabaseEngineId): number {
  if (srid === null || !Number.isInteger(srid) || srid < 0) {
    throw makeSpatialFilterUnsupportedError(engine);
  }
  return srid;
}

/**
 * MSSQL SRIDs whose coordinates are DEGREES of longitude/latitude, so a
 * `geometry`-typed column in one of them cannot answer a metre-based distance
 * without being read as `geography` first (see `buildDistanceSql`).
 *
 * Deliberately an allowlist of the common lon/lat datums rather than "every
 * SRID SQL Server calls geography" (393 of them): anything NOT listed keeps
 * today's raw-`geometry` emit, which is the correct form for a projected SRS
 * whose units are already linear. Adding an SRID here asserts two things —
 * its coordinates are degrees, and `sys.spatial_reference_systems` accepts it
 * for `geography` (verified for all four on SQL Server 2025).
 */
const MSSQL_LONLAT_SRIDS = new Set([
  4326, // WGS 84
  4269, // NAD 83
  4258, // ETRS 89
  4267, // NAD 27
]);

/** Reduce a longitude into `[-180, 180]`, leaving in-range values (incl. ±180) untouched. */
function wrapLng(x: number): number {
  if (x >= -180 && x <= 180) return x;
  return ((((x + 180) % 360) + 360) % 360) - 180;
}

export type Envelope = [number, number, number, number]; // [minLng, minLat, maxLng, maxLat]

/**
 * Antimeridian split (A29/G12), width-preserving (F1). The box WIDTH — not the
 * wrapped endpoints — drives the result, so a 360°-span box stays whole-world
 * instead of collapsing to a zero-width sliver (wrapping each endpoint alone
 * maps both `-190` and `170` → `170`). A crossing box (east past `+180`) splits
 * into `[west,s,180,n]` + `[-180,s,east-360,n]`; a single envelope for a
 * crossing box is WRONG on every engine. Never clamps (drops crossing data).
 */
export function normalizeEnvelopes([minLng, minLat, maxLng, maxLat]: Envelope): Envelope[] {
  let width = maxLng - minLng;
  if (width < 0) width += 360; // crossing encoded as west>east (both in-range)
  if (width >= 360) return [[-180, minLat, 180, maxLat]]; // whole world
  const west = wrapLng(minLng);
  const eastUnwrapped = west + width; // may exceed +180
  if (eastUnwrapped <= 180) return [[west, minLat, eastUnwrapped, maxLat]];
  return [
    [west, minLat, 180, maxLat],
    [-180, minLat, eastUnwrapped - 360, maxLat],
  ];
}

// ---- per-engine bbox predicate (one envelope) --------------------------------

function pgBbox(g: string, [w, s, e, n]: Envelope, engine: DatabaseEngineId): string {
  const env = `ST_MakeEnvelope(${num(w, engine)},${num(s, engine)},${num(e, engine)},${num(n, engine)},4326)`;
  const t = `ST_Transform(${env}, ST_SRID(${g}))`;
  return `${g} && ${t} AND ST_Intersects(${g}, ${t})`;
}

function mysqlBbox(g: string, [w, s, e, n]: Envelope, engine: DatabaseEngineId): string {
  const W = num(w, engine), S = num(s, engine), E = num(e, engine), N = num(n, engine);
  const ring = `${W} ${S},${E} ${S},${E} ${N},${W} ${N},${W} ${S}`;
  return `MBRIntersects(${g}, ST_GeomFromText('POLYGON((${ring}))', 4326, 'axis-order=long-lat'))`;
}

function oracleBbox(g: string, [w, s, e, n]: Envelope, engine: DatabaseEngineId): string {
  const ord = `${num(w, engine)},${num(s, engine)},${num(e, engine)},${num(n, engine)}`;
  return (
    `SDO_GEOM.RELATE(${g}, 'ANYINTERACT', ` +
    `SDO_GEOMETRY(2003,4326,NULL,SDO_ELEM_INFO_ARRAY(1,1003,3),SDO_ORDINATE_ARRAY(${ord})), 0.05) = 'TRUE'`
  );
}

function mssqlBbox(g: string, [w, s, e, n]: Envelope, kind: string, srid: number, engine: DatabaseEngineId): string {
  const W = num(w, engine), S = num(s, engine), E = num(e, engine), N = num(n, engine);
  const ring = `${W} ${S}, ${E} ${S}, ${E} ${N}, ${W} ${N}, ${W} ${S}`;
  const type = kind === 'geography' ? 'geography' : 'geometry';
  return `${g}.STIntersects(${type}::STGeomFromText('POLYGON ((${ring}))', ${srid})) = 1`;
}

function sqliteBbox(g: string, [w, s, e, n]: Envelope, engine: DatabaseEngineId): string {
  return `MbrIntersects(${g}, BuildMbr(${num(w, engine)},${num(s, engine)},${num(e, engine)},${num(n, engine)}))`;
}

// ---- bbox dispatch (handles the antimeridian split) --------------------------

/**
 * Build the bbox predicate for `box`, delegating to the per-engine builder and
 * OR-ing the antimeridian-split envelopes. `latField` is `g` for declared pairs
 * (the declared latitude column); `lngField` is the sibling longitude column.
 */
export function buildBboxSql(
  g: string,
  ctx: SpatialFilterContext,
  box: Envelope,
  engine: DatabaseEngineId,
  lngField?: string,
): string {
  const envelopes = normalizeEnvelopes(box);

  if (ctx.kind === 'declared-pair') {
    if (!lngField) throw makeSpatialFilterUnsupportedError(engine);
    const latRange = `${g} >= ${num(envelopes[0][1], engine)} AND ${g} <= ${num(envelopes[0][3], engine)}`;
    const lngParts = envelopes.map((e) => `${lngField} >= ${num(e[0], engine)} AND ${lngField} <= ${num(e[2], engine)}`);
    const lngExpr = lngParts.length === 1 ? `(${lngParts[0]})` : `(${lngParts.map((p) => `(${p})`).join(' OR ')})`;
    return `(${lngExpr} AND ${latRange})`;
  }

  switch (engine) {
    case 'pgsql': {
      const preds = envelopes.map((e) => pgBbox(g, e, engine));
      return preds.length === 1 ? preds[0] : `((${preds[0]}) OR (${preds[1]}))`;
    }
    case 'mysql': {
      const preds = envelopes.map((e) => mysqlBbox(g, e, engine));
      return preds.length === 1 ? preds[0] : `(${preds.join(' OR ')})`;
    }
    case 'oracle': {
      const preds = envelopes.map((e) => oracleBbox(g, e, engine));
      return preds.length === 1 ? preds[0] : `(${preds.join(' OR ')})`;
    }
    case 'mssql': {
      const srid = mssqlSrid(ctx.srid, engine);
      const preds = envelopes.map((e) => mssqlBbox(g, e, ctx.kind, srid, engine));
      return preds.length === 1 ? preds[0] : `(${preds.join(' OR ')})`;
    }
    case 'sqlite': {
      const preds = envelopes.map((e) => sqliteBbox(g, e, engine));
      return preds.length === 1 ? preds[0] : `(${preds.join(' OR ')})`;
    }
    default:
      throw makeSpatialFilterUnsupportedError(engine);
  }
}

// ---- distance dispatch -------------------------------------------------------

/** Build the within-distance predicate (`meters` around `point` [lng, lat]). */
export function buildDistanceSql(
  g: string,
  ctx: SpatialFilterContext,
  point: [number, number],
  meters: number,
  engine: DatabaseEngineId,
): string {
  // Longitude is wrapped into [-180,180] like the bbox path (F3) — a center at
  // lng 190 emits the normalized -170. Latitude is NOT wrapped.
  const lng = num(wrapLng(point[0]), engine);
  const lat = num(point[1], engine);
  const m = num(meters, engine);
  switch (engine) {
    case 'pgsql':
      // Reference point adopts the column's own SRID (F2) — a 4269 geography
      // column would otherwise hit PostGIS "Operation on mixed SRID
      // geographies". ST_SRID(g) resolves to 4326 for a 4326 column, so the
      // geometry/geography-4326 byte behaviour is preserved.
      return `ST_DWithin(${g}::geography, ST_SetSRID(ST_MakePoint(${lng},${lat}),ST_SRID(${g}))::geography, ${m})`;
    case 'mysql':
      return `ST_Distance_Sphere(${g}, ST_SRID(POINT(${lng},${lat}),4326)) <= ${m}`;
    case 'oracle':
      return `SDO_GEOM.SDO_DISTANCE(${g}, SDO_GEOMETRY(2001,4326,SDO_POINT_TYPE(${lng},${lat},NULL),NULL,NULL), 0.05, 'unit=M') <= ${m}`;
    case 'mssql': {
      const srid = mssqlSrid(ctx.srid, engine);
      if (ctx.kind !== 'geography' && MSSQL_LONLAT_SRIDS.has(srid)) {
        // `geometry.STDistance` answers in the SRS's OWN units, and for a
        // lon/lat SRS those are DEGREES — so `<= 100000` (metres) was true for
        // every row on Earth and the chip filtered nothing. Measured on SQL
        // Server 2025, geometry/4326: Warsaw→Berlin 7.61, Warsaw→Lisbon 33.04;
        // as geography the same pair is 518837 m. Casting to geography is what
        // makes `distanceMeters` mean metres, and matches what the other
        // engines already do (pg `::geography`, mysql `ST_Distance_Sphere`,
        // oracle `unit=M`). Pinned by
        // features/mssql/spatial-filters.realDb.spec.ts → "100 km distance
        // chip around Warsaw keeps only Warsaw".
        //
        // `MakeValid()` first: a self-intersecting ring is legal as geometry
        // but NOT as geography, and without it the conversion throws
        // mid-query ("A .NET Framework error occurred … geography") instead of
        // filtering. Verified no-op for points/linestrings.
        return (
          `geography::STGeomFromWKB(${g}.MakeValid().STAsBinary(), ${srid})` +
          `.STDistance(geography::STGeomFromText('POINT (${lng} ${lat})', ${srid})) <= ${m}`
        );
      }
      // geography column → STDistance is already metres. Non-lon/lat geometry
      // (projected, e.g. 3857/UTM, or SRID 0) → its own units are already
      // linear, so the raw call is right and geography cannot hold it anyway.
      const type = ctx.kind === 'geography' ? 'geography' : 'geometry';
      return `${g}.STDistance(${type}::STGeomFromText('POINT (${lng} ${lat})', ${srid})) <= ${m}`;
    }
    default:
      throw makeSpatialFilterUnsupportedError(engine);
  }
}
